import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faEyeSlash, faFrown, faRefresh } from '@/fontawesome';
import { APIRequestError } from '@/services/api';
import CourseBreadcrumb from '@/components/videos/CourseBreadcrumb';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import VideoListCard from '@/components/videos/VideoListCard';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import { APIRoutes } from '@/services/api.routes';
import type { Course, Module, Video, CourseID, ModuleID } from '@/models';

const PAGE_SIZE = 50;

const ModuleDetail: React.FC = () => {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [module, setModule] = useState<Module | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyUnwatched, setShowOnlyUnwatched] = useState(false);
  const [videoProgress, setVideoProgress] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (courseId && moduleId) {
      fetchModuleData();
    }
  }, [courseId, moduleId, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showOnlyUnwatched]);

  const fetchModuleData = async () => {
    if (!courseId || !moduleId) return;

    try {
      setLoading(true);
      setError(null);
      const [courseResponse, moduleResponse, videosResponse, , moduleProgressResponse] = await Promise.all([
        APIRoutes.getCourse(courseId as CourseID),
        APIRoutes.getModule(moduleId as ModuleID),
        APIRoutes.listVideos(moduleId as ModuleID, {
          published: true,
          limit: PAGE_SIZE,
          next: currentPage > 1 ? `$${currentPage}` : undefined
        }),
        // Mark module as started
        APIRoutes.startModule(moduleId as ModuleID).catch(err => {
          console.error('Failed to mark module as started:', err);
          return null;
        }),
        // Get module progress from API
        APIRoutes.getModuleProgress(moduleId as ModuleID).catch(err => {
          console.error('Failed to get module progress:', err);
          return null;
        })
      ]);

      setCourse(courseResponse);
      setModule(moduleResponse);
      setVideos(videosResponse.videos);
      setTotalVideos(videosResponse.total);

      // Create a map of video progress from the API response
      const videoProgressArray = moduleProgressResponse?.videoProgress ?? [];
      const videoProgressMap = new Map<string, { videoID: string; progress: number }>(
        videoProgressArray.map(vp => [vp.videoID, vp])
      );

      // Initialize progress tracking based on API data
      // Backend returns progress as 0.0-1.0, videos are considered watched at >= 0.95 (95%)
      const progress: Record<string, boolean> = {};

      videosResponse.videos.forEach((video: Video) => {
        const videoProgress = videoProgressMap.get(video.id);
        progress[video.id] = videoProgress !== undefined && videoProgress.progress >= 0.95;
      });

      setVideoProgress(progress);
    } catch (err) {
      console.error('Error loading module:', err);
      if (err instanceof APIRequestError && err.errorcode === 'NOT_PUBLISHED') {
        setError('NOT_PUBLISHED');
      } else {
        setError('GENERIC');
      }
      setCourse(null);
      setModule(null);
    } finally {
      setLoading(false);
    }
  };

  // Don't redirect immediately, wait for the data to load
  if (!courseId || !moduleId) {
    return <Navigate to="/courses" replace />;
  }

  // Show error states
  if (error === 'NOT_PUBLISHED') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
            <FontAwesomeIcon icon={faEyeSlash} className="w-8 h-8 text-gray-600 dark:text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Modul nicht verfügbar
          </h1>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
            Dieses Modul ist derzeit nicht veröffentlicht.
          </p>
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <Link
              to={courseId ? `/courses/${courseId}` : '/courses'}
              className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
              Zurück zur Kursübersicht
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error === 'GENERIC') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          <div className="text-6xl mb-6">
            <FontAwesomeIcon icon={faFrown} className="text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Fehler beim Laden
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
            Das Modul konnte nicht geladen werden. Bitte versuchen Sie es erneut.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={fetchModuleData}
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faRefresh} className="w-4 h-4 mr-2" />
              Erneut versuchen
            </button>
            <Link
              to={courseId ? `/courses/${courseId}` : '/courses'}
              className="inline-flex items-center justify-center px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
              Zurück
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // If data is still loading, show loading state instead of redirecting
  if (!course || !module) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Lade Videos...</p>
        </div>
      </>
    );
  }

  const filteredVideos = videos.filter((video: Video) => {
    const matchesSearch = searchQuery === '' ||
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.description.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;

    if (showOnlyUnwatched) {
      return !videoProgress[video.id];
    }

    return true;
  });

  const displayedVideoCount = videos.length;
  // Only count progress for videos that are actually shown (published videos)
  const watchedVideos = videos.filter(video => videoProgress[video.id]).length;
  const progress = displayedVideoCount > 0 ? Math.round((watchedVideos / displayedVideoCount) * 100) : 0;

  return (
    <>
      <div>
        <CourseBreadcrumb
          items={[
            { label: 'Kurse', href: '/courses' },
            { label: course.title, href: `/courses/${courseId}` },
            { label: module.title }
          ]}
        />

        <div className="mb-8">
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <Link
                    to={`/courses/${courseId}`}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
                  </Link>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {module.title}
                  </h1>
                </div>
                {module.description && (
                  <div className="ml-9">
                    <MarkdownRenderer>
                      {module.description}
                    </MarkdownRenderer>
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 ml-9 md:ml-0 md:shrink-0">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {progress}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Modulfortschritt
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {watchedVideos} von {totalVideos} Videos
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Videos durchsuchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
              />
            </div>

            <button
              onClick={() => setShowOnlyUnwatched(!showOnlyUnwatched)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 cursor-pointer ${
                showOnlyUnwatched
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
              }`}
            >
              <FontAwesomeIcon icon={faFilter} className="w-4 h-4" />
              Nur nicht gesehene
            </button>
          </div>
        </div>

        {filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery || showOnlyUnwatched
                ? 'Keine Videos gefunden. Versuche es mit anderen Suchkriterien.'
                : 'Noch keine Videos verfügbar.'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {filteredVideos.map((video, index) => (
                <VideoListCard
                  key={video.id}
                  video={video}
                  videoNumber={(currentPage - 1) * PAGE_SIZE + index + 1}
                  isWatched={videoProgress[video.id]}
                  courseId={courseId}
                  moduleId={moduleId}
                />
              ))}
            </div>
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={totalVideos}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </>
        )}
      </div>
    </>
  );
};

export default ModuleDetail;
